import React, { useEffect, useMemo, useState } from 'react';

export default function DeepResearchPlayground() {
  const [input, setInput] = useState('');
  const [agent, setAgent] = useState('deep-research-pro-preview-12-2025');
  const [interactionId, setInteractionId] = useState('');
  const [status, setStatus] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const brandId = useMemo(() => {
    try {
      return (
        localStorage.getItem('currentBrandId') ||
        localStorage.getItem('selectedBrandId') ||
        sessionStorage.getItem('currentBrandId') ||
        sessionStorage.getItem('selectedBrandId') ||
        ''
      );
    } catch (_) {
      return '';
    }
  }, []);

  const start = async () => {
    const prompt = String(input || '').trim();
    if (!prompt || loading) return;
    setLoading(true);
    setError('');
    setOutput('');
    setStatus('starting');
    try {
      const resp = await fetch('https://api.glowytics.com/api/ai/gemini/deep-research/start', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include',
        body: JSON.stringify({ input: prompt, agent })
      });
      const txt = await resp.text();
      let json;
      try { json = JSON.parse(txt); } catch { json = { message: txt }; }
      if (!resp.ok) throw new Error(json?.error || json?.details?.error || json?.details?.message || resp.statusText);

      setInteractionId(json?.interactionId || '');
      setStatus(json?.status || 'running');
    } catch (e) {
      setError(e?.message || 'Failed to start deep research');
      setStatus('failed');
    } finally {
      setLoading(false);
    }
  };

  // Poll status while running
  useEffect(() => {
    let timer = null;
    let cancelled = false;

    const poll = async () => {
      try {
        if (!interactionId) return;
        const qs = new URLSearchParams({ interactionId });
        const resp = await fetch(`https://api.glowytics.com/api/ai/gemini/deep-research/status?${qs.toString()}`, {
          headers: { 'X-Brand-ID': brandId },
          credentials: 'include'
        });
        const txt = await resp.text();
        let json;
        try { json = JSON.parse(txt); } catch { json = {}; }
        if (!resp.ok) throw new Error(json?.error || resp.statusText);
        if (cancelled) return;
        setStatus(json?.status || '');
        if (json?.output) setOutput(json.output);
        if (json?.status === 'completed' || json?.status === 'failed') {
          if (timer) clearInterval(timer);
        }
      } catch (e) {
        if (cancelled) return;
        setError(e?.message || 'Polling failed');
      }
    };

    if (interactionId && status !== 'completed' && status !== 'failed') {
      poll();
      timer = setInterval(poll, 10000);
    }
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [interactionId, status, brandId]);

  return (
    <div className="p-6">
      <div className="max-w-5xl mx-auto">
        <div className="mb-5">
          <div className="text-xl font-semibold text-gray-900">Deep Research (Gemini)</div>
          <div className="text-sm text-gray-500">
            Runs an async research agent and returns a long-form report (can take minutes). Docs: <code>interactions</code>.
          </div>
        </div>

        {!!error && (
          <div className="mb-4 text-xs px-3 py-2 rounded-md bg-amber-50 text-amber-700 border border-amber-200">
            {error}
          </div>
        )}

        <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
          <div className="p-4 border-b bg-gray-50 flex flex-wrap items-center gap-3">
            <div className="text-xs text-gray-600">Agent</div>
            <select
              className="px-2 py-1 rounded-lg border bg-white text-xs"
              value={agent}
              onChange={(e) => setAgent(e.target.value)}
              disabled={loading}
            >
              <option value="deep-research-pro-preview-12-2025">deep-research-pro-preview-12-2025</option>
            </select>

            <div className="ml-auto flex items-center gap-2">
              {!!status && (
                <div className="text-xs px-2 py-1 rounded-lg border bg-white text-gray-700">
                  {status}
                </div>
              )}
              <button
                className="px-3 py-2 text-xs rounded-lg bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
                onClick={start}
                disabled={loading || !String(input || '').trim()}
              >
                Start research
              </button>
            </div>
          </div>

          <div className="p-4">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              rows={5}
              placeholder="Describe what you want researched. Be specific about format (sections/tables/citations)."
              className="w-full px-3 py-2 text-sm border rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300"
            />

            {!!interactionId && (
              <div className="mt-3 text-[11px] text-gray-500 break-all">
                interactionId: <code>{interactionId}</code>
              </div>
            )}

            <div className="mt-5">
              <div className="text-sm font-medium text-gray-900 mb-2">Output</div>
              <div className="min-h-[260px] whitespace-pre-wrap text-sm rounded-xl border bg-gray-50 p-3 text-gray-900">
                {output || (status ? 'Working… (polling every ~10s)' : 'Start a research task to see output here.')}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-3 text-[11px] text-gray-500">
          Note: Deep Research runs via Interactions API with <code>background=true</code> and polling. See docs at{' '}
          <a className="underline" href="https://ai.google.dev/gemini-api/docs/deep-research" target="_blank" rel="noreferrer">ai.google.dev</a>.
        </div>
      </div>
    </div>
  );
}


